import {
  buildAcuityGisIframeUrl,
  getAcuityFlow,
  AcuityFlow,
} from "@app/pages/private/scheduleAGeneticInformationSession/buildAcuityUrl";
import { Language } from "@app/provider";
import { Config } from "@app/provider/config";
import { routes } from "@app/routing";
import { TestType } from "@app/provider/testData/types";
import { UpdatePrefilledDataInput } from "@app/service/intakeForm";
import { validateEmail, validateZipCode } from "@app/utils";
import { convertPhoneNumberToE164 } from "@app/utils/convertPhoneNumber";
import { getGisScheduleLink } from "@app/utils/getGisScheduleLink";
import {
  GIS_ENTRY_SOURCE_ORDER_LOOKUP,
  isWithinGisRecencyWindow,
} from "@app/utils/gisEntrySource";
import { PublicGisLookupErrorType, PublicGisMatchedOrder } from "./types";

interface LookupPrefillSource {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  mobileNumber?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
}

/**
 * Maps what the visitor typed in the lookup form to the persisted prefill
 * patch — every contact detail they entered, not just the one they chose to be
 * matched on, since this snapshot is what later fills their booking.
 *
 * Each contact value is held to the rule its column is validated by, because
 * only the identifier used for matching was checked by the form: the other two
 * can hold a half-typed draft left behind when the visitor switched chips.
 * Dropping a bad draft costs that one field, where sending it would fail
 * validation for the whole patch — and the funnel deliberately ignores that
 * failure, so every field would be lost instead of one.
 *
 * Phone is judged by the caller's `usePhoneValidation`, the same check the
 * forms gate on, so a number a visitor was allowed to submit is never one this
 * then discards.
 */
export function buildLookupPrefilledInput(
  data: LookupPrefillSource | null | undefined,
  isValidPhone: (phone: string) => boolean
): UpdatePrefilledDataInput {
  const enteredPhone = data?.mobileNumber || data?.phone;
  const phone = enteredPhone
    ? convertPhoneNumberToE164(enteredPhone)
    : undefined;
  const email = data?.email?.trim();
  const zipCode = data?.zipCode?.trim();

  return {
    firstName: data?.firstName || undefined,
    lastName: data?.lastName || undefined,
    dateOfBirth: data?.dateOfBirth || undefined,
    phone: phone && isValidPhone(phone) ? phone : undefined,
    // Plus-addressing is allowed whatever the form's own stricter setting
    // says, matching the `@IsEmail` the column is stored behind.
    email: email && validateEmail(email, true) ? email : undefined,
    zipCode: zipCode && validateZipCode(zipCode) ? zipCode : undefined,
  };
}

/**
 * Collapses a list of matched orders to one per test type, keeping the latest
 * (newest `createdAt`) of each. Mirrors the authenticated multi-order picker's
 * `getEligibleOrders` rules: orders older than three months are excluded, and
 * GIS counseling is product-specific, so multiple orders of the same test type
 * add no value to the picker.
 */
export function collapseOrdersToLatestPerTestType(
  orders: PublicGisMatchedOrder[]
): PublicGisMatchedOrder[] {
  const sorted = orders
    .filter((order) => isWithinGisRecencyWindow(order.createdAt))
    .sort(
      (a, b) =>
        new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
    );
  const seen = new Set<string>();
  return sorted.filter((order) => {
    const key = String(order.testType);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Picker order bridge
//
// The intake runs on its own routes, which mount a component tree without the
// funnel's provider, so the orders behind the picker have to outlive the trip
// for Back to be able to show the visitor that list again. Same sessionStorage
// lifetime, and the same order-bound tokens, as the guest session the funnel
// already stashes before handing off.
// ---------------------------------------------------------------------------

const LOOKUP_MATCHED_ORDERS_KEY = "gis_guest_lookup_orders";

export function writeLookupMatchedOrders(
  orders: PublicGisMatchedOrder[]
): void {
  try {
    sessionStorage.setItem(LOOKUP_MATCHED_ORDERS_KEY, JSON.stringify(orders));
  } catch {
    // Ignore quota / unavailable sessionStorage.
  }
}

export function readLookupMatchedOrders(): PublicGisMatchedOrder[] {
  try {
    const raw = sessionStorage.getItem(LOOKUP_MATCHED_ORDERS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return parsed as PublicGisMatchedOrder[];
  } catch {
    // Ignore parse / unavailable errors.
  }
  return [];
}

export function clearLookupMatchedOrders(): void {
  try {
    sessionStorage.removeItem(LOOKUP_MATCHED_ORDERS_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Names the funnel screen in the URL, giving each one a history entry of its
 * own so Back off any of them is the browser's ordinary Back.
 */
export const LOOKUP_STEP_PARAM = "step";
export const LOOKUP_STEP_TESTS = "tests";
export const LOOKUP_STEP_SCHEDULE = "schedule";

/**
 * Funnel screen the intake was entered from: the list of the visitor's tests
 * if they chose one there, the lookup form otherwise. Named rather than read
 * off history, which by the time a visitor backs out of the intake also holds
 * the intake screens they walked forward and back through.
 */
export function resolveLookupReturnUrl(): string {
  return readLookupMatchedOrders().length >= 2
    ? `${routes.publicGisOrderLookup}?${LOOKUP_STEP_PARAM}=${LOOKUP_STEP_TESTS}`
    : routes.publicGisOrderLookup;
}

/** Maps a GraphQL/GuestMP error code to a terminal funnel error type. */
export function mapErrorCode(code?: string): PublicGisLookupErrorType {
  switch (code) {
    case "MP_NO_PATIENT_MATCH":
      return "MP_NO_PATIENT_MATCH";
    case "MP_NO_ORDER_FOUND":
      return "MP_NO_ORDER_FOUND";
    case "MP_NO_MATCHING_ORDER":
      return "MP_NO_MATCHING_ORDER";
    case "MP_MULTIPLE_ORDERS":
      return "MP_MULTIPLE_ORDERS";
    case "MP_ACTIVE_ALREADY_EXISTS":
      return "MP_ACTIVE_ALREADY_EXISTS";
    default:
      return "MP_GENERIC_NO_MATCH_ERROR";
  }
}

/**
 * Resolves the Acuity flow (ONC vs WH/OH Empower) for a matched order from its
 * test type, reusing the shared `getAcuityFlow` mapping.
 */
export function resolveLookupFlow(order: PublicGisMatchedOrder): AcuityFlow {
  return getAcuityFlow(order.testType);
}

/**
 * Renasight is GIS-eligible but is scheduled through its own external form
 * rather than the ONC intake flow or the WH Acuity scheduler, matching how
 * authenticated triage routes a Renasight order. Returns that form's URL for a
 * Renasight order and null for every product the funnel schedules in-app.
 */
export function resolveLookupExternalScheduleUrl(
  order: PublicGisMatchedOrder,
  config: Config,
  currentLanguage: Language
): string | null {
  if (order.testType !== TestType.RENASIGHT) return null;
  return getGisScheduleLink(TestType.RENASIGHT, config, currentLanguage);
}

/**
 * Builds the Acuity scheduling URL a matched WH/OH order is sent to, always
 * stamping `schedulingSourceValue = Public_Entry_Order_Lookup` and using the
 * server-resolved pre/post-test counseling intent and result code (the latter
 * routes Empower negative/carrier and Panorama low-risk patients to their
 * result-specific sessions). The funnel navigates the page to it rather than
 * embedding it, so Acuity's own history entries stay out of this tab's stack.
 */
export function buildLookupScheduleUrl(order: PublicGisMatchedOrder): string {
  return buildAcuityGisIframeUrl({
    testType: order.testType,
    status: order.status,
    counselingIntent: order.counselingIntent,
    resultCode: order.resultCode,
    schedulingSourceValue: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
  });
}
